/**
 * Cookie consent (ADR-0048). The choice is kept in localStorage (`prop.consent`) and mirrored in a first-party cookie
 * (`fl_consent`), for 180 days; then the banner asks again. Strictly necessary storage needs no consent and is always on.
 * Optional categories: analytics and marketing (nothing uses them today — scripts added later must check
 * `hasConsent()` and listen for the change event), and external content (the TradingView chart view of the terminal).
 * Every read and write is best effort: blocked storage just means no remembered choice.
 */
import { useSyncExternalStore } from "react";

export type ConsentCategory = "analytics" | "marketing" | "external";
export type Consent = Record<ConsentCategory, boolean> & { at: number };

const KEY = "prop.consent";
const COOKIE = "fl_consent";
const MAX_AGE_S = 180 * 86_400;
const CHANGE = "prop:consent";
const OPEN = "prop:consent-open";
const CATEGORIES: readonly ConsentCategory[] = ["analytics", "marketing", "external"];

/** Cookie form: "1.<analytics><marketing><external>.<unix seconds>", e.g. 1.001.1767225600. */
function encode(c: Consent): string {
  return `1.${CATEGORIES.map((k) => (c[k] ? "1" : "0")).join("")}.${Math.floor(c.at / 1000)}`;
}

function decode(v: string | null | undefined): Consent | null {
  const m = v?.match(/^1\.([01]{3})\.(\d{9,11})$/);
  if (!m) return null;
  const at = Number(m[2]) * 1000;
  return { analytics: m[1][0] === "1", marketing: m[1][1] === "1", external: m[1][2] === "1", at };
}

function fresh(c: Consent | null): Consent | null {
  return c && Date.now() - c.at < MAX_AGE_S * 1000 && c.at <= Date.now() + 86_400_000 ? c : null;
}

function read(): Consent | null {
  try {
    const own = fresh(decode(localStorage.getItem(KEY)));
    if (own) return own;
  } catch {
    // storage blocked: try the cookie
  }
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]+)`));
  return fresh(decode(m ? decodeURIComponent(m[1]) : null));
}

let current: Consent | null | undefined;

function snapshot(): Consent | null {
  if (current === undefined) current = read();
  return current;
}

export function saveConsent(choice: Record<ConsentCategory, boolean>): void {
  const c: Consent = { ...choice, at: Date.now() };
  const v = encode(c);
  try {
    localStorage.setItem(KEY, v);
  } catch {
    // not persisted in storage; the cookie still holds it
  }
  document.cookie = `${COOKIE}=${v}; Max-Age=${MAX_AGE_S}; Path=/; SameSite=Lax${location.protocol === "https:" ? "; Secure" : ""}`;
  current = c;
  window.dispatchEvent(new Event(CHANGE));
}

/** True when the visitor allowed this optional category. Strictly necessary storage never asks. */
export function hasConsent(category: ConsentCategory): boolean {
  return snapshot()?.[category] ?? false;
}

function subscribe(fn: () => void): () => void {
  window.addEventListener(CHANGE, fn);
  return () => window.removeEventListener(CHANGE, fn);
}

/** The stored choice (null: not chosen yet or expired), re-rendering on change; the object changes only on save. */
export function useConsent(): Consent | null {
  return useSyncExternalStore(subscribe, snapshot, () => null);
}

/** Opens the cookie settings (footer and legal pages "Cookie settings"). */
export function openConsentSettings(): void {
  window.dispatchEvent(new Event(OPEN));
}

export function onConsentSettingsOpen(fn: () => void): () => void {
  window.addEventListener(OPEN, fn);
  return () => window.removeEventListener(OPEN, fn);
}
